// «QUIÉN NO ACUMULA» (F1, 19-oct-2026, #1465 Silvestre). Marcar/cambiar a un cliente como empleado, influencer u otro:
// puede seguir registrándose, pero sus visitas quedan en 0 y su saldo se congela (no canjea) mientras esté marcado.
// Si no viene `contact`, el modal empieza con un buscador (lo abre la tarjeta de Mi Negocio › Cómo registro visitas).
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ban, Loader2, Search, X } from 'lucide-react';
import { accrualBlocksApi, type AccrualBlock, type AccrualReason } from '../../../api/accrualBlocksApi';
import { clientsApi } from '../../../api/clientsApi';

interface Props {
  contact?: { id: string; name: string } | null;
  current?: AccrualBlock | null;
  onClose: () => void;
  onSaved: (b: AccrualBlock) => void;
}

const MOTIVOS: { v: AccrualReason; t: string }[] = [
  { v: 'empleado', t: 'Empleado' }, { v: 'influencer', t: 'Influencer' }, { v: 'otro', t: 'Otro' },
];
const hoyYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const AccrualBlockModal: React.FC<Props> = ({ contact: contactProp, current, onClose, onSaved }) => {
  const [contact, setContact] = useState(contactProp || null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ id: string; name: string; phone: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [reason, setReason] = useState<AccrualReason>(current?.reason || 'empleado');
  const [note, setNote] = useState(current?.note || '');
  const [conFecha, setConFecha] = useState(!!current?.ends_on);
  const [hasta, setHasta] = useState(current?.ends_on || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Buscador (solo cuando se abre desde la tarjeta, sin cliente elegido).
  useEffect(() => {
    if (contact) return;
    const t = q.trim();
    if (t.length < 2) { setResults([]); return; }
    let vivo = true;
    setSearching(true);
    const h = setTimeout(() => {
      clientsApi.list({ q: t, size: 8 })
        .then((r) => { if (vivo) setResults((r.clients || []).map((c) => ({ id: String(c.id), name: c.visitorName, phone: c.phoneNumber }))); })
        .catch(() => { if (vivo) setResults([]); })
        .finally(() => { if (vivo) setSearching(false); });
    }, 300);
    return () => { vivo = false; clearTimeout(h); };
  }, [q, contact]);

  const valido = !!contact && (!conFecha || (/^\d{4}-\d{2}-\d{2}$/.test(hasta) && hasta >= hoyYmd()));

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valido || busy || !contact) return;
    setBusy(true); setError('');
    try {
      const b = await accrualBlocksApi.save(contact.id, { reason, note: note.trim() || null, ends_on: conFecha ? hasta : null });
      onSaved(b);
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar');
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={guardar} data-testid="no-acumula-modal"
        className="modal-sheet w-full max-h-[92vh] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <Ban size={18} className="text-amber-500" /> No acumula visitas
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>

        {!contact ? (
          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-content-2">Busca al cliente por nombre o teléfono</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="no-acumula-buscar"
                placeholder="Ana, 686 123…"
                className="w-full rounded-lg border border-line bg-surface-2 py-2.5 pl-9 pr-3 text-sm text-content outline-none focus:border-accent/50" />
            </div>
            {searching && <p className="mt-2 flex items-center gap-1 text-xs text-content-3"><Loader2 size={12} className="animate-spin" /> Buscando…</p>}
            {results.length > 0 && (
              <ul className="mt-2 divide-y divide-line rounded-lg border border-line">
                {results.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => setContact({ id: r.id, name: r.name })}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-2">
                      <span className="truncate text-content">{r.name}</span>
                      <span className="ml-2 shrink-0 text-xs text-content-3">{r.phone}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searching && q.trim().length >= 2 && results.length === 0 && (
              <p className="mt-2 text-xs text-content-3">Sin resultados. El cliente tiene que haberse registrado al menos una vez.</p>
            )}
          </div>
        ) : (
          <p className="mb-4 text-sm text-content-2">
            <b className="text-content">{contact.name}</b> podrá seguir registrándose, pero sus visitas no sumarán puntos y no podrá
            canjear premios mientras esté marcado. Lo que ya tiene se conserva.
            {!contactProp && (
              <button type="button" onClick={() => { setContact(null); }} className="ml-1 text-xs text-accent hover:underline">Cambiar</button>
            )}
          </p>
        )}

        <fieldset disabled={!contact} className="disabled:opacity-50">
          <label className="mb-1 block text-xs font-medium text-content-2">Motivo</label>
          <div className="mb-3 grid grid-cols-3 gap-1.5">
            {MOTIVOS.map((m) => (
              <button key={m.v} type="button" onClick={() => setReason(m.v)} aria-pressed={reason === m.v} data-testid={`no-acumula-${m.v}`}
                className={`rounded-lg border px-3 py-2 text-sm ${reason === m.v ? 'border-amber-500 bg-amber-500/10 font-semibold text-amber-700 dark:text-amber-300' : 'border-line text-content-2 hover:bg-surface-2'}`}>
                {m.t}
              </button>
            ))}
          </div>

          <label className="mb-1 block text-xs font-medium text-content-2">Nota (opcional, solo la ve tu equipo)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} data-testid="no-acumula-nota"
            placeholder={reason === 'empleado' ? 'barista turno mañana' : reason === 'influencer' ? 'colaboración de octubre' : 'cuenta de prueba'}
            className="mb-3 w-full rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50" />

          <label className="mb-1 block text-xs font-medium text-content-2">Hasta</label>
          <div className="mb-2 space-y-1.5 text-sm text-content-2">
            <label className="flex items-center gap-2">
              <input type="radio" checked={!conFecha} onChange={() => setConFecha(false)} /> Hasta que lo quite
            </label>
            <label className="flex flex-wrap items-center gap-2">
              <input type="radio" checked={conFecha} onChange={() => setConFecha(true)} /> Hasta el
              <input type="date" value={hasta} min={hoyYmd()} onChange={(e) => { setHasta(e.target.value); setConFecha(true); }}
                data-testid="no-acumula-hasta"
                className="rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-sm text-content outline-none focus:border-accent/50" />
            </label>
          </div>
          <p className="mb-3 text-[11px] text-content-3">
            Al vencer la fecha vuelve a acumular solo. No afecta los puntos que gane por traer referidos ni los ajustes que hagas tú a mano.
          </p>
        </fieldset>

        {error && <p className="mb-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="submit" disabled={!valido || busy} data-testid="no-acumula-guardar"
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} Guardar
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default AccrualBlockModal;

// Chip pequeño para listas (Lista de clientes, Visitas por QR).
export const NoAcumulaChip: React.FC<{ block?: AccrualBlock | null; className?: string }> = ({ block, className = '' }) =>
  block ? (
    <span title={`No acumula · ${block.reason_label}${block.ends_on ? ` (hasta ${block.ends_on})` : ''}`} data-testid="chip-no-acumula"
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 ${className}`}>
      <Ban size={10} /> No acumula
    </span>
  ) : null;
